import React, {CSSProperties, useEffect, useRef} from 'react'

import Colors from '@utils/Colors.js'
import L from './leaflet.js'
import {baseMapOptions, createTileLayer, defaultCenter, defaultZoom} from './setup.js'
import {samePosition, validPosition} from './geometry.js'
import useLeafletMap from './hooks/useLeafletMap.js'
import useLatest from './hooks/useLatest.js'
import MapControls from './controls/MapControls.js'
import MapAttribution from './controls/MapAttribution.js'
import {mapContainerStyle, mapElementStyle} from './styles.js'

export interface LocationPickerProps {
    /** The position of the marker, the centre of the Netherlands when missing */
    value?: {lat?: number, lng?: number} | null
    /** Called with the new position when the marker is dragged */
    onChange: (position: {lat: number, lng: number}) => void
    /** Place of the map in the layout, 400 high by default */
    style?: CSSProperties
}

const markerIcon = L.divIcon({
    html: `<i style="font-size: 38px; color: ${Colors.buttonSolid};" class="mdi mdi-map-marker"></i>`,
    iconSize: [38, 38],
    iconAnchor: [19, 38],
    // No class, Leaflet's default one gives the icon a white box
    className: ''
})

/**
 * A map with a marker to drag to a position (an address whose geocoded position is overridden). The map follows a
 * value that changes from outside, not the marker the user just dragged; a value equal to the shown one does
 * nothing, so a new object every render is fine.
 */
export default function LocationPicker({value, onChange, style}: LocationPickerProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const latest = useLatest({onChange})
    const markerRef = useRef<L.Marker | null>(null)
    const position = validPosition(value) || defaultCenter

    const map = useLeafletMap(containerRef, baseMapOptions, (map) => {
        map.setView(position, defaultZoom)
        createTileLayer().addTo(map)

        const marker = L.marker(position, {icon: markerIcon, draggable: true}).addTo(map)

        marker.on('dragend', () => {
            const {lat, lng} = marker.getLatLng()
            latest.current.onChange({lat, lng})
        })

        markerRef.current = marker

        return () => {
            markerRef.current = null
        }
    })

    useEffect(() => {
        const marker = markerRef.current

        if (!map || !marker || samePosition(marker.getLatLng(), position)) {
            return
        }

        marker.setLatLng(position)
        map.setView(position)
    }, [map, position.lat, position.lng])

    return (
        <div style={{...mapContainerStyle, height: 400, ...style}}>
            <div ref={containerRef} style={mapElementStyle} />
            <MapControls map={map} />
            <MapAttribution />
        </div>
    )
}
